import type {
  AssistantDiscoveryKind,
  AssistantDiscoveryTarget,
} from '@revelio/core/global-ai-assistant/discovery-targets';
import type {
  ReportParamResolvedSelection,
  ReportParamSuggestion,
} from '@revelio/core/global-ai-assistant/reports';

import { dashMetaFetch } from '../../shared/api/dash-meta-fetch';

export type GlobalAssistantEntityType =
  | 'company'
  | 'industry'
  | 'geography'
  | 'role'
  | AssistantDiscoveryTarget['entityType'];
export type GlobalAssistantContextPolicy = 'full' | 'recent_only' | 'none';

export type CustomDashAssistantEntityType =
  | 'company'
  | 'industry'
  | 'geography'
  | 'role';

export type SingleChartAssistantCapabilityEntityType = Exclude<
  CustomDashAssistantEntityType,
  'industry'
>;

export type ScreenerAssistantEntityType = Exclude<
  CustomDashAssistantEntityType,
  'industry'
>;

export type GlobalAssistantDiscoveryKind = AssistantDiscoveryKind;

export type GlobalAssistantWarmUpResponse = {
  status: 'started' | 'in_flight' | 'already_warm';
  model?: string;
  ttlMs?: number;
  warmedAgeMs?: number;
};

export function toCustomDashAssistantEntityType(
  entityType: GlobalAssistantEntityType | undefined
): CustomDashAssistantEntityType | undefined {
  if (
    entityType === 'company' ||
    entityType === 'industry' ||
    entityType === 'geography' ||
    entityType === 'role'
  ) {
    return entityType;
  }
  return undefined;
}

export function toSingleChartAssistantCapabilityEntityType(
  entityType: GlobalAssistantEntityType | undefined
): SingleChartAssistantCapabilityEntityType | undefined {
  const customDashEntityType = toCustomDashAssistantEntityType(entityType);
  return customDashEntityType === 'industry' ? 'company' : customDashEntityType;
}

export function toScreenerAssistantEntityType(
  entityType: GlobalAssistantEntityType | undefined
): ScreenerAssistantEntityType | undefined {
  if (
    entityType === 'company' ||
    entityType === 'geography' ||
    entityType === 'role'
  ) {
    return entityType;
  }
  return undefined;
}

export type GlobalAssistantSuggestMatch = {
  pageId: string;
  name: string;
  navigatePath: string;
  confidenceScore: number;
  applyFiltersAfterNavigate?: boolean;
  discoveryKind?: GlobalAssistantDiscoveryKind;
  resolvedPromptForApply?: string;
};

export type GlobalAssistantConversationTurn = {
  role: 'user' | 'assistant';
  content: string;
};

export type GlobalAssistantCurrentPageContext =
  | {
      pathname: string;
      kind: 'discovery';
      discoveryKind: GlobalAssistantDiscoveryKind;
      baseDiscoveryPrompt?: string;
      baseFilterPrompt?: string;
      /** `__std__:` id of this page, when the catalog describes one. */
      matchedOptionId?: string;
    }
  | {
      pathname: string;
      kind: 'custom_dash';
      baseFilterPrompt?: string;
      /**
       * `__std__:` id when this "custom dash" path is really a standard entity
       * page (`/company/pay`, `/industry/compositions`, …) — those share this
       * kind. Undefined for an actual custom dashboard.
       */
      matchedOptionId?: string;
    }
  | {
      pathname: string;
      kind: 'home';
    }
  | {
      pathname: string;
      kind: 'other';
      /** `__std__:` id of this page, when the catalog describes one. */
      matchedOptionId?: string;
    };

export type GlobalAssistantReportOffer = {
  reportType: string;
  reportName: string;
  confidence: number;
  sampleLink: string;
  description: string;
};

export type GlobalAssistantReportResolvedSelection =
  ReportParamResolvedSelection;

export type GlobalAssistantReportParamSuggestion = ReportParamSuggestion;

export type GlobalAssistantReportPrefillMetrics = {
  extractedCount: number;
  resolvedCount: number;
  appliedCount: number;
  skippedCount: number;
  warnings?: GlobalAssistantReportPrefillWarning[];
};

export type GlobalAssistantReportPrefillWarning = {
  parameterId: string;
  parameterDisplayName: string;
  selectionName: string;
  foundLevelId?: string;
  supportedLevelIds?: string[];
  selectionListId?: string;
  reason: 'level_id_mismatch' | 'company_not_in_list';
};

export async function postGlobalAssistantReportParams(body: {
  prompt: string;
  reportType: string;
  rolesTax?: string;
  skillsTax?: number;
}): Promise<GlobalAssistantReportParamSuggestion[]> {
  const response = await dashMetaFetch(
    '/api/build-a-dash/ai-gen/global-assistant/report-params',
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }
  return response.json() as Promise<GlobalAssistantReportParamSuggestion[]>;
}

export type GlobalAssistantSelectReportResponse = {
  offers: Array<{
    reportType: string;
    confidence: number;
    reportName?: string;
    description?: string;
  }>;
  suppressionReason?: string;
};

export async function postGlobalAssistantSelectReport(body: {
  prompt: string;
}): Promise<GlobalAssistantSelectReportResponse> {
  const response = await dashMetaFetch(
    '/api/build-a-dash/ai-gen/global-assistant/select-report',
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }
  return response.json() as Promise<GlobalAssistantSelectReportResponse>;
}

export async function postGlobalAssistantWarmUp(): Promise<GlobalAssistantWarmUpResponse> {
  const response = await dashMetaFetch(
    '/api/build-a-dash/ai-gen/global-assistant/warm-up',
    {
      method: 'POST',
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }
  return response.json() as Promise<GlobalAssistantWarmUpResponse>;
}

export async function postGlobalAssistantSummarizeConversation(
  body: { turns: GlobalAssistantConversationTurn[] },
  signal?: AbortSignal
): Promise<{ summary: string }> {
  const response = await dashMetaFetch(
    '/api/build-a-dash/ai-gen/global-assistant/summarize-conversation',
    {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }
  return response.json() as Promise<{ summary: string }>;
}
